import { OktaAuth } from '@okta/okta-auth-js';
import Emitter from 'eventemitter3';

// Allow users to namespace the storage
var namespaceSessionProvider = (namespace) => {
    // Init storage
    window.sessionStorage[namespace] = '{}';
    function getNamespace() {
        return JSON.parse(window.sessionStorage[namespace]);
    }
    return {
        getItem: function (key) {
            const authStorage = getNamespace();
            return JSON.stringify(authStorage[key]);
        },
        setItem: function (key, val) {
            const authStorage = getNamespace();
            authStorage[key] = JSON.parse(val);
            const value = JSON.stringify(authStorage);
            window.sessionStorage.setItem(namespace, value);
        },
        // optional
        removeItem: function (key) {
            const authStorage = getNamespace();
            delete authStorage[key];
            window.sessionStorage.setItem(namespace, authStorage);
        },
    };
};

const defaultScopes = ['profile openid email'];
const defaultConfig = {
    qa: {
        issuer: 'https://nike-qa.oktapreview.com/oauth2/ausa0mcornpZLi0C40h7',
        // issuer: 'https://api.aegis.nikecloud.com/v1/qa',
        // authorizeUrl: 'https://api.aegis.nikecloud.com/v1/qa/authorize',
        // tokenUrl: 'https://api.aegis.nikecloud.com/v1/qa/token',
    },
    prod: {
        issuer: 'https://nike.okta.com/oauth2/aus27z7p76as9Dz0H1t7',
        // issuer: 'https://api.aegis.nikecloud.com/v1/prod',
        //   authorizeUrl: 'https://api.aegis.nikecloud.com/v1/prod/authorize',
        //   tokenUrl: 'https://api.aegis.nikecloud.com/v1/prod/token',
    },
};
async function httpRequestClient(method, url, args) {
    const fetchParams = { method, ...args };
    if (url.includes('.well-known/openid-configuration') || url.includes('token')) {
        delete fetchParams.credentials;
        delete fetchParams.headers['X-Okta-User-Agent-Extended'];
    }
    if (args.data) {
        fetchParams.body = args.data;
        delete fetchParams.data;
    }
    const response = await fetch(url, fetchParams);
    const json = await response.json();
    return { responseText: json };
}
const events = [
    'login:expired',
    'login:error',
    'login:success',
    'logout:success',
    'renew:access:success',
    'renew:id:success',
    'renew:error',
];
const pendingPromise = new Promise((resolve) => { });
/*
  Events

  Okta Events
    expired: -> login:expired - Fired before a token is set to expire (using expireEarlySeconds option, 30 seconds by default). If autoRenew option is set to true, a listener will be attached to this event and an attempt will be made to renew the token when the event fires.
    error: -> renew:error - Fired when a token renew attempt has failed. This is a permanent error, and the token will be removed from storage.
    renewed: -> renew:success - Fired when a token has been renewed by the tokenManager, either via the autoRenew process or as a result of calling tokenManager.renew
    removed: unsused - Fired when a token is removed from storage as a result of renew failure, or a call to tokenManager.remove. (This event will not fire from tokenManager.clear)
  Custom Events
    login:success -> fired after login
    login:error - > fired when login fails
    logout:success -> fired after logout clears tokens
*/
function StorageType(disableTokenStorage, namespace) {
    if (disableTokenStorage) {
        return 'memory';
    }
    else if (namespace && namespace.length > 0) {
        return namespaceSessionProvider(namespace);
    }
    return 'sessionStorage';
}
class AegisClient {
    constructor({ clientId, redirectUri, qa = false, disableTokenStorage = false, pkce = true, responseType, storageNamespace, scopes = defaultScopes, config, ...rest }) {
        const environmentConfig = config || qa ? defaultConfig.qa : defaultConfig.prod;
        this._okta = new OktaAuth({
            clientId,
            redirectUri,
            responseType,
            pkce,
            httpRequestClient,
            issuer: environmentConfig.issuer,
            tokenManager: {
                storage: StorageType(disableTokenStorage, storageNamespace),
            },
            ...rest
        });
        this._events = new Emitter();
        this._scopes = scopes;
        this._okta.tokenManager.on('error', (...args) => this._events.emit('renew:error', ...args));
        this._okta.tokenManager.on('renewed', (type, ...args) => {
            switch (type) {
                case 'accessToken':
                    return this._events.emit('renew:access:success', ...args);
                case 'idToken':
                    return this._events.emit('renew:id:success', ...args);
            }
        });
        this._okta.tokenManager.on('expired', (...args) => this._events.emit('login:expired', ...args));
        window.addEventListener('unhandledrejection', ({ reason }) => {
            // in reason object there is also: name, errorCode and message - these can be used for error handling
            this._events.emit('login:error', reason);
        });
    }
    async init(authorizeOptions = {}) {
        // Login redirect means we came from okta
        if (this._okta.isLoginRedirect()) {
            await this._okta.handleLoginRedirect();
            this._okta.start();
            return this.finalizeLogin();
        }
        // Existing login may be in session storage
        const isAuthenticated = await this._okta.isAuthenticated();
        if (isAuthenticated) {
            this._okta.start();
            return this.finalizeLogin();
        }
        // Otherwise login
        return this.login(authorizeOptions);
    }
    async login(authorizeOptions = {}) {
        try {
            await this._okta.signInWithRedirect({
                scopes: this._scopes,
                ...authorizeOptions,
            });
            // Don't resolve, just wait for the redirect to leave the page
            await pendingPromise;
        }
        catch (e) {
            this._events.emit('login:error', e);
        }
    }
    async logout() {
        this._okta.tokenManager.clear();
        this._events.emit('logout:success');
    }
    async getAccessToken() {
        return this._okta.tokenManager.get('accessToken');
    }
    async getIdToken() {
        return this._okta.tokenManager.get('idToken');
    }
    on(event, handler) {
        validateEvent(event);
        this._events.on(event, handler);
    }
    once(event, handler) {
        validateEvent(event);
        this._events.once(event, handler);
    }
    off(event, handler) {
        validateEvent(event);
        this._events.off(event, handler);
    }
    async finalizeLogin() {
        const [accessToken, idToken] = await Promise.all([this.getAccessToken(), this.getIdToken()]);
        this._events.emit('login:success', {
            accessToken,
            idToken,
        });
        return {
            accessToken,
            idToken,
        };
    }
}
function validateEvent(event) {
    if (!events.includes(event))
        throw new Error(`event must be one of: ${events.join(', ')}`);
}

export { AegisClient };
